<template>
    <key-value-list
      v-if="isLoading || consolidatedFields.length > 0"
      v-bind="$attrs"
      is-divided
      :variant="variant"
      :size="size">
    <v-progress-circular v-if="showLoading && isLoading" color="secondary" indeterminate size="16" width="2" />
    <template v-if="!isLoading">
      <custom-forms-multiple-trigger-form-field
          v-for="formField in consolidatedFields"
          :key="formField.id"
          :data-testid="`custom-forms-field-${formField.testId}`"
          :components-mapping="componentsMapping"
          :entity-name="formField.entityName"
          :form-field="formField"
          :timezone="timezone"
          :military-time-enabled="militaryTimeEnabled"
          :variant="fieldVariant"
          :always-show-edit-button="alwaysShowEditButton"
          @update:formField="handleFormFieldUpdate"
          :is-editable="isEditable"
          :is-loading="isLoading" />
      </template>
    </key-value-list>
</template>
<script>
import { ref, defineComponent, onBeforeMount, onBeforeUnmount, getCurrentInstance } from 'vue';
import { isEmpty, isDefined } from 'class-validator';
import {
  filterActiveFormFields,
  getCustomFieldFormattedValue,
  isOrphanedCustomFormData,
  CustomFieldType
} from '@nova/core';
import customFormsService from '@satellite/services/custom-forms-service.js';
import CustomFormsMultipleTriggerFormField from './CustomFormsMultipleTriggerFormField.vue';
import { KeyValueList } from '@satellite/components/elements/key_value';
import { $t } from '@satellite/plugins/translator';

export default defineComponent({
  name: 'CustomFormsMultipleTriggerForm',
  components: {
    CustomFormsMultipleTriggerFormField,
    KeyValueList
  },
  emits: ['update:formField'],
  props: {
    objectId: {
      type: String,
      required: true
    },
    warehouseId: {
      type: String,
      required: true
    },
    triggers: {
      type: Array,
      required: true
    },
    timezone: {
      type: String,
      required: true
    },
    militaryTimeEnabled: {
      type: Boolean,
      required: true
    },
    variant: {
      type: String,
      required: false,
      default: 'list',
      validator(value) {
        return ['list', 'list-stacked', 'list-inline', 'grid'].includes(value);
      }
    },
    size: {
      type: String,
      required: false,
      default: 'regular',
      validator(value) {
        return ['regular', 'small'].includes(value);
      }
    },
    isEditable: {
      type: Boolean,
      required: false,
      default: false
    },
    fieldVariant: {
      type: String,
      required: false,
      default: 'plain',
      validator(value) {
        return ['card', 'plain', 'none'].includes(value);
      }
    },
    componentsMapping: {
      type: Object,
      required: false,
      default: () => ({})
    },
    alwaysShowEditButton: {
      type: Boolean,
      required: false,
      default: false
    },
    showLoading: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  setup(props, { emit }) {
    const consolidatedFields = ref([]);
    const isLoading = ref(false);

    // Immutable field slot definitions built from currently-active FormField records.
    // Kept separately so getCustomFormData can rebuild from a clean slate on every call
    // without accumulating mutations from previous fetches.
    const activeFieldSlots = ref([]);

    /**
     * This is a "hack" so we can access the eventHub from a Component API-based
     * component.
     */
    const getEventHub = () => {
      let eventHub = null;

      try {
        const app = getCurrentInstance()?.appContext.app;
        eventHub = app.config.globalProperties.$eventHub;
      } catch (error) {
        console.error('[CustomFormsMultipleTriggerForm] Error retrieving eventHub instance', error);
      }

      return eventHub;
    };

    const getCustomFormData = async () => {
      isLoading.value = true;

      if (props.objectId && props.triggers?.length > 0) {
        try {
          // Fetch all CFD rows for this object and triggers, including rows whose
          // formFieldId was set to null by the FK cascade when a FormField was deleted
          // (e.g. on field inactivation). The denormalized label/type/value on those
          // rows is preserved and should still be visible as historical read-only data.
          const formData = await customFormsService.getCustomFormData({
            s: {
              objectId: props.objectId,
              triggerId: { $in: props.triggers.map(t => t.id) }
            },
            limit: 1000
          });

          // Rebuild active field rows from the clean slot definitions each time,
          // then match each slot to its corresponding submitted CFD row.
          const activeWithValues = activeFieldSlots.value.map(slot => {
            const formField = { ...slot };
            const existingFormDataField = formData.find(
              f => f.formField?.id === formField.id && f.triggerId === formField.triggerId
            );
            const trigger = props.triggers.find(t => t.id === formField.triggerId);

            formField.label = formField.overrideLabel || formField.field.label;
            formField.objectId = props.objectId;
            formField.warehouseId = props.warehouseId;
            formField.testId = formField.id;
            formField.entityName = trigger?.dataEntityName;

            if (existingFormDataField) {
              formField.value = existingFormDataField.value;
              formField.label = formField.overrideLabel || formField.field.label;
              formField.type = formField.field.type;
              // This will be used to update the value on customformdata
              formField.dataId = existingFormDataField.id;

              formField.formattedValue = formattedValue(formField);
            } else {
              formField.formattedValue = null;
            }

            return formField;
          });

          activeWithValues.sort((a, b) => a.order - b.order);

          // Determine which CFD rows were already matched to an active field slot.
          const matchedCfdIds = new Set(
            activeWithValues
              .filter(formField => formField.dataId)
              .map(formField => formField.dataId)
          );

          // Surface orphaned historical rows — CFD rows whose FormField was deleted
          // (formField is null after SET NULL cascade) but whose snapshot data is intact.
          // These are rendered as read-only entries; CustomFormsMultipleTriggerFormField
          // already disables editing when formField.field?.id is falsy.
          const historicalEntries = formData
            .filter(
              f =>
                !matchedCfdIds.has(f.id) &&
                isOrphanedCustomFormData(f) &&
                isDefined(f.value) &&
                f.value !== ''
            )
            .map(f => ({
              id: f.id,
              label: f.label,
              type: f.type,
              value: f.value,
              triggerId: f.triggerId,
              objectId: props.objectId,
              warehouseId: props.warehouseId,
              testId: f.id,
              entityName: props.triggers.find(t => t.id === f.triggerId)?.dataEntityName,
              // Empty field object keeps formField.field?.id falsy so the child
              // component renders this entry as non-editable.
              field: {},
              formattedValue: formattedValue({ value: f.value, type: f.type, label: f.label }),
              required: false,
              // Place historical entries after all active fields.
              order: Infinity
            }));

          consolidatedFields.value = [...activeWithValues, ...historicalEntries];
        } catch (error) {
          console.error($t('customForms.common.messages.errorFetchingCustomFormData'), error);
        } finally {
          isLoading.value = false;
        }
      }
    };

    const formattedValue = field => {
      if (
        field.value === '' ||
        !isDefined(field.value) ||
        isEmpty(field.value) ||
        (Array.isArray(field.value) && field.value.length === 0)
      ) {
        return null;
      }
      return getCustomFieldFormattedValue(field, {
        [CustomFieldType.Document]: { generateLink: true },
        [CustomFieldType.MultiDocument]: { generateLink: true },
        [CustomFieldType.Timestamp]: {
          timezone: props.timezone,
          formatAsMilitary: props.militaryTimeEnabled
        }
      });
    };

    const setFields = async () => {
      // Record the currently-active field slot definitions. These are stored separately
      // from consolidatedFields so getCustomFormData can rebuild from a clean baseline
      // on every call (rather than re-mapping already-mutated entries).
      activeFieldSlots.value = props.triggers.flatMap(trigger =>
        filterActiveFormFields(trigger.flow?.formFrom?.formFields).map(formField => ({
          ...formField,
          triggerId: trigger.id
        }))
      );

      // Fetch the submitted values, up to this point, we have the triggers
      // Which contains the fields definition
      await getCustomFormData();
    };

    const handleFormFieldUpdate = async $event => {
      await getCustomFormData();
      emit('update:formField', $event);
    };

    const handleCustomFormDataUpdated = event => {
      const triggers = Array.isArray(props.triggers) ? props.triggers : [];
      const needsToFetchCustomFormData = triggers.some(trigger => {
        return trigger.id === event.triggerId;
      });

      if (needsToFetchCustomFormData) {
        getCustomFormData();
      }
    };

    onBeforeMount(() => {
      setFields();

      const eventHub = getEventHub();
      if (eventHub) {
        eventHub.$on('create-CustomFormData', handleCustomFormDataUpdated);
        eventHub.$on('update-CustomFormData', handleCustomFormDataUpdated);
      }
    });

    onBeforeUnmount(() => {
      const eventHub = getEventHub();
      if (eventHub) {
        eventHub.$off('create-CustomFormData', handleCustomFormDataUpdated);
        eventHub.$off('update-CustomFormData', handleCustomFormDataUpdated);
      }
    });

    return {
      isLoading,
      consolidatedFields,
      formattedValue,
      handleFormFieldUpdate
    };
  }
});
</script>
